
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';


/**
 * Preferencias horarias del usuario de Nexus.
 *
 * Los campos pueden ser NULL porque así los
 * definimos en la migración de Laravel.
 */
export interface TimezonePreferences {
  timezone?: string | null;
  timezone_mode?: 'automatic' | 'manual' | null;
}

export interface TimezoneUpdateResponse {
  message: string;
  timezone: string;
  timezone_mode: 'automatic' | 'manual';
}

/**
 * Servicio centralizado de zonas horarias.
 *
 * REGLA GENERAL:
 * - Laravel y MySQL trabajan con UTC.
 * - Angular presenta los horarios al usuario.
 * - Las fechas agendadas se envían como ISO UTC.
 * - Las fechas puras, como date_of_birth,
 *   no deben convertirse.
 */
@Injectable({
  providedIn: 'root'
})
export class TimezoneService {

  private readonly api =
    `${environment.apiUrl}/me/timezone`;

  private readonly fallbackTimezone = 'UTC';

  constructor(private http: HttpClient) {}

  /**
   * Detecta la zona horaria del navegador.
   *
   * Ejemplos:
   * America/Bogota
   * America/Caracas
   * Europe/Rome
   * America/New_York
   */
  getBrowserTimezone(): string {
    const timezone =
      Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone;

    return this.isValidTimezone(timezone)
      ? timezone
      : this.fallbackTimezone;
  }

  /**
   * Comprueba que la zona horaria sea válida.
   */
  isValidTimezone(timezone: string): boolean {
    try {
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone
      });

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Obtiene la zona efectiva del usuario.
   *
   * MANUAL:
   * Usa la zona guardada en Nexus.
   *
   * AUTOMATIC:
   * Usa la zona actual del navegador.
   *
   * NULL:
   * Se interpreta como modo automático,
   * tal como acordamos para usuarios antiguos.
   */
  getEffectiveTimezone(
    user?: TimezonePreferences | null
  ): string {

    if (
      user?.timezone_mode === 'manual' &&
      user.timezone &&
      this.isValidTimezone(user.timezone)
    ) {
      return user.timezone;
    }

    return this.getBrowserTimezone();
  }

  /**
   * Obtiene las partes de un instante
   * representadas en una zona horaria.
   *
   * Se utiliza para convertir horarios
   * manuales sin depender del país donde
   * esté físicamente el dispositivo.
   */
  private getParts(
    timestamp: number,
    timezone: string
  ): number[] {

    const formatter = new Intl.DateTimeFormat(
      'en-US',
      {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      }
    );

    const parts = formatter.formatToParts(
      new Date(timestamp)
    );

    const get = (type: string): number =>
      Number(
        parts.find(p => p.type === type)?.value
      );

    return [
      get('year'),
      get('month'),
      get('day'),
      get('hour'),
      get('minute'),
      get('second')
    ];
  }

  /**
   * Convierte una fecha local a UTC.
   *
   * Entrada:
   * 2026-10-28T17:30
   *
   * Zona:
   * America/Bogota
   *
   * Resultado:
   * 2026-10-28T22:30:00.000Z
   *
   * IMPORTANTE:
   * Detecta horarios inexistentes o ambiguos
   * durante cambios de horario de verano.
   * No selecciona silenciosamente una hora.
   */
  localToUtc(
    localDateTime: string,
    timezone: string
  ): string {

    if (!this.isValidTimezone(timezone)) {
      throw new Error('Invalid timezone.');
    }

    const match = localDateTime.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
    );

    if (!match) {
      throw new Error('Invalid local datetime format.');
    }

    const values = match.slice(1).map(value =>
      value === undefined ? 0 : Number(value)
    );

    const [
      year,
      month,
      day,
      hour,
      minute,
      second = 0
    ] = values;

    const localAsUtc = Date.UTC(
      year,
      month - 1,
      day,
      hour,
      minute,
      second
    );

    const target = [
      year, month, day, hour, minute, second
    ];

    const validDate = new Date(localAsUtc);

    if (
      validDate.getUTCFullYear() !== year ||
      validDate.getUTCMonth() + 1 !== month ||
      validDate.getUTCDate() !== day ||
      validDate.getUTCHours() !== hour ||
      validDate.getUTCMinutes() !== minute ||
      validDate.getUTCSeconds() !== second
    ) {
      throw new Error('Invalid local datetime.');
    }

    /*
     * Recopilamos los offsets posibles
     * alrededor de la fecha solicitada.
     *
     * Es importante porque las zonas con
     * horario de verano pueden tener dos
     * offsets diferentes en el mismo día.
     */
    const offsets = new Set<number>();

    for (let h = -48; h <= 48; h += 6) {
      const sample =
        localAsUtc + h * 3600000;

      const p = this.getParts(sample, timezone);

      const represented = Date.UTC(
        p[0], p[1] - 1, p[2],
        p[3], p[4], p[5]
      );

      offsets.add(represented - sample);
    }

    /*
     * Probamos todos los offsets posibles.
     * Solo aceptamos instantes cuya fecha
     * local coincida exactamente.
     */
    const matches: number[] = [];

    for (const offset of offsets) {
      const candidate = localAsUtc - offset;

      const parts = this.getParts(
        candidate,
        timezone
      );

      if (
        parts.every(
          (value, index) =>
            value === target[index]
        )
      ) {
        matches.push(candidate);
      }
    }

    if (matches.length === 0) {
      throw new Error(
        'This local time does not exist in the selected timezone.'
      );
    }

    if (matches.length > 1) {
      throw new Error(
        'This local time is ambiguous due to a daylight saving time change.'
      );
    }

    return new Date(matches[0]).toISOString();
  }

  /**
   * Convierte una fecha local a UTC utilizando
   * las preferencias actuales del usuario.
   *
   * Ideal para:
   * - Callback
   * - Tasks
   * - Calendar
   * - Appointments
   */
  localToUtcForUser(
    localDateTime: string,
    user?: TimezonePreferences | null
  ): string {

    return this.localToUtc(
      localDateTime,
      this.getEffectiveTimezone(user)
    );
  }

  /**
   * Interpreta una fecha UTC proveniente
   * del backend.
   *
   * Exigimos una zona explícita:
   * Z, +00:00, -05:00, etc.
   *
   * Evitamos interpretar cadenas sin zona
   * porque JavaScript podría tratarlas
   * como hora local del dispositivo.
   */
  private parseInstant(value: string): Date {

    if (
      !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/i
        .test(value)
    ) {
      throw new Error(
        'Datetime must include an explicit timezone.'
      );
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      throw new Error('Invalid UTC datetime.');
    }

    return date;
  }

  /**
   * Convierte un instante UTC al formato
   * requerido por input datetime-local.
   *
   * Resultado:
   * YYYY-MM-DDTHH:mm
   *
   * Útil para editar agendamientos.
   */
  utcToLocalInput(
    utcDateTime: string,
    timezone: string
  ): string {

    const date = this.parseInstant(utcDateTime);

    const parts = this.getParts(
      date.getTime(),
      timezone
    );

    const pad = (value: number) =>
      String(value).padStart(2, '0');

    return (
      `${parts[0]}-${pad(parts[1])}-${pad(parts[2])}` +
      `T${pad(parts[3])}:${pad(parts[4])}`
    );
  }

  /**
   * Formatea una fecha UTC para mostrarla.
   *
   * Permite indicar idioma y formato.
   *
   * Ejemplo:
   * 28/10/2026, 5:30 p. m.
   */
  formatUtc(
    utcDateTime: string,
    user?: TimezonePreferences | null,
    locale = 'es-CO',
    hour12 = true
  ): string {

    const date = this.parseInstant(utcDateTime);

    return new Intl.DateTimeFormat(
      locale,
      {
        timeZone: this.getEffectiveTimezone(user),
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12
      }
    ).format(date);
  }

  /**
   * Actualiza las preferencias del usuario
   * mediante el endpoint de Laravel.
   *
   * PATCH /api/me/timezone
   *
   * No modifica otros campos del perfil.
   */
  updatePreferences(
    preferences: {
      timezone: string;
      timezone_mode: 'automatic' | 'manual';
      user_id?: number | string;
    }
      ): Observable<TimezoneUpdateResponse> {

      const token =
          localStorage.getItem('token') ||
          sessionStorage.getItem('token') ||
          '';

      const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
      });

      return this.http.patch<TimezoneUpdateResponse>(
          this.api,
          preferences,
          { headers }
      );
  }

  /**
   * Sincroniza la zona horaria detectada por el navegador.
   *
   * No modifica usuarios que utilizan modo manual.
   * No envía una petición si la zona sigue siendo la misma.
   *
   * Retorna null cuando no es necesaria la sincronización.
   */
  syncAutomaticTimezone(
    user: TimezonePreferences | null
  ): Observable<TimezoneUpdateResponse> | null {

    if (user?.timezone_mode === 'manual') {
      return null;
    }

    const browserTimezone = this.getBrowserTimezone();

    if (
      user?.timezone_mode === 'automatic' &&
      user.timezone === browserTimezone
    ) {
      return null;
    }

    return this.updatePreferences({
      timezone: browserTimezone,
      timezone_mode: 'automatic'
    });
  }
}
